import {
    DataType,
    DataTypeOperator,
} from "crqlar-react-common/dist/models/audience/filter.model";
import type {
    FilterOption,
    PredefinedValue,
} from "crqlar-react-common/dist/models/audience/filterOption.model";
import {GenderType} from "crqlar-react-common/dist/models/profile/profile.model";
import {ReservationStatus} from "crqlar-react-common/dist/models/reservation/reservation.model";
import type {TFunction} from "i18next";

const ENUM_OPERATORS: DataTypeOperator[] = [
    DataTypeOperator.EQUAL,
    DataTypeOperator.NOT_EQUAL,
    DataTypeOperator.IN,
    DataTypeOperator.NOT_IN,
];

const TIMESTAMP_OPERATORS: DataTypeOperator[] = [
    DataTypeOperator.MORE_THAN_EQUAL,
    DataTypeOperator.LESS_THAN_EQUAL,
    DataTypeOperator.BETWEEN,
    DataTypeOperator.NOT_BETWEEN,
];

const NUMBER_OPERATORS: DataTypeOperator[] = [
    DataTypeOperator.EQUAL,
    DataTypeOperator.NOT_EQUAL,
    DataTypeOperator.MORE_THAN,
    DataTypeOperator.MORE_THAN_EQUAL,
    DataTypeOperator.LESS_THAN,
    DataTypeOperator.LESS_THAN_EQUAL,
    DataTypeOperator.BETWEEN,
    DataTypeOperator.NOT_BETWEEN,
];

const SPA_RESERVATION_STATUSES: ReservationStatus[] = [
    ReservationStatus.PENDING,
    ReservationStatus.CONFIRMED,
    ReservationStatus.ACTIVE,
    ReservationStatus.COMPLETED,
    ReservationStatus.CANCELLED,
    ReservationStatus.NO_SHOW,
];

const RESTAURANT_RESERVATION_STATUSES: ReservationStatus[] = [
    ReservationStatus.PENDING,
    ReservationStatus.CONFIRMED,
    ReservationStatus.ACTIVE,
];

// The CRM daily list carries every status, so every one of them is offered here: this filter is the
// only way to narrow the board down, and a status missing from the list would be a status nobody
// could hide.
const CRM_RESERVATION_STATUSES: ReservationStatus[] = Object.values(ReservationStatus);

export const predefinedValue = (
    cid: string,
    name: string,
    colorCode = "",
): PredefinedValue => ({cid, name, colorCode});

interface DailyListFilterContext {
    t: TFunction;
    locale: string;
    rooms: PredefinedValue[];
}

interface SpaDailyListFilterContext extends DailyListFilterContext {
    services: PredefinedValue[];
    staff: PredefinedValue[];
    facilities: PredefinedValue[];
}

interface RestaurantDailyListFilterContext extends DailyListFilterContext {
    restaurants: PredefinedValue[];
    tables: PredefinedValue[];
}

const localizations = (locale: string, name: string) => ({[locale]: name});

const enumOption = (
    field: string,
    name: string,
    values: PredefinedValue[],
    locale: string,
): FilterOption => ({
    field,
    name,
    localizations: localizations(locale, name),
    description: name,
    allowedOperators: ENUM_OPERATORS,
    predefinedValues: values,
    dataType: DataType.ENUM,
});

const existsOption = (
    field: string,
    name: string,
    locale: string,
): FilterOption => ({
    field,
    name,
    localizations: localizations(locale, name),
    description: name,
    allowedOperators: [DataTypeOperator.EXISTS, DataTypeOperator.NOT_EXISTS],
    predefinedValues: [],
    dataType: DataType.TEXT,
});

const timestampOption = (
    field: string,
    name: string,
    locale: string,
): FilterOption => ({
    field,
    name,
    localizations: localizations(locale, name),
    description: name,
    allowedOperators: TIMESTAMP_OPERATORS,
    predefinedValues: [],
    dataType: DataType.TIMESTAMP,
});

const numberOption = (
    field: string,
    name: string,
    locale: string,
): FilterOption => ({
    field,
    name,
    localizations: localizations(locale, name),
    description: name,
    allowedOperators: NUMBER_OPERATORS,
    predefinedValues: [],
    dataType: DataType.NUMBER,
});

const reservationStatusValues = (t: TFunction, statuses: ReservationStatus[]) => (
    statuses.map(status => predefinedValue(status, t(`Reservation status ${status}`)))
);

const createGuestFilterSections = ({
    t,
    locale,
    rooms,
}: DailyListFilterContext, arrivalField: string, departureField: string) => ({
    identity: [
        {
            field: "languageCodes",
            name: t("Languages"),
            localizations: localizations(locale, t("Languages")),
            description: t("Languages"),
            allowedOperators: [DataTypeOperator.IN, DataTypeOperator.NOT_IN],
            predefinedValues: [],
            dataType: DataType.TEXT,
            idType: "language_code_iso_639_1",
        },
    ] satisfies FilterOption[],
    accommodation: [
        timestampOption(arrivalField, t("Arrival"), locale),
        timestampOption(departureField, t("Departure"), locale),
        ...(rooms.length ? [enumOption("room", t("Room"), rooms, locale)] : []),
    ],
    profile: [
        numberOption("age", t("Age"), locale),
        existsOption("allergies", t("Allergies"), locale),
        existsOption("diets", t("Diets"), locale),
        enumOption(
            "gender",
            t("Gender"),
            Object.values(GenderType).map(gender => predefinedValue(
                gender,
                t(`Gender type ${gender}`),
            )),
            locale,
        ),
        existsOption("medical", t("Medical"), locale),
        enumOption(
            "recommendationSeverity",
            t("Recommendations"),
            [
                predefinedValue("WARNING", t("Warning")),
                predefinedValue("INFORMATION", t("Information")),
                predefinedValue("SUCCESS", t("Success")),
            ],
            locale,
        ),
        existsOption("voiceNotes", t("Voice Notes"), locale),
    ],
});

export const createCrmDailyListFilterOptions = (
    context: DailyListFilterContext,
): FilterOption[] => {
    const sections = createGuestFilterSections(context, "start", "end");
    const reservationOptions: FilterOption[] = [
        enumOption(
            "reservationStatus",
            context.t("Reservation status"),
            reservationStatusValues(context.t, CRM_RESERVATION_STATUSES),
            context.locale,
        ),
    ];

    return [
        ...sections.identity,
        ...sections.accommodation,
        ...reservationOptions,
        ...sections.profile,
    ];
};

export const createSpaDailyListFilterOptions = ({
    t,
    locale,
    services,
    staff,
    facilities,
    ...guestContext
}: SpaDailyListFilterContext): FilterOption[] => {
    const sections = createGuestFilterSections(
        {t, locale, ...guestContext},
        "stayStart",
        "stayEnd",
    );
    const reservationOptions: FilterOption[] = [
        enumOption(
            "reservationStatus",
            t("Reservation status"),
            reservationStatusValues(t, SPA_RESERVATION_STATUSES),
            locale,
        ),
        timestampOption("start", t("Start"), locale),
        timestampOption("end", t("End"), locale),
        ...(services.length ? [enumOption("serviceId", t("Service"), services, locale)] : []),
        ...(staff.length ? [enumOption("staffResourceId", t("Staff"), staff, locale)] : []),
        ...(facilities.length ? [enumOption("facilityResourceId", t("Facility"), facilities, locale)] : []),
        numberOption("guestCount", t("Guest count"), locale),
    ];

    return [
        ...sections.identity,
        ...sections.accommodation,
        ...reservationOptions,
        ...sections.profile,
    ];
};

export const createRestaurantDailyListFilterOptions = ({
    t,
    locale,
    restaurants,
    tables,
    ...guestContext
}: RestaurantDailyListFilterContext): FilterOption[] => {
    const sections = createGuestFilterSections(
        {t, locale, ...guestContext},
        "stayStart",
        "stayEnd",
    );
    const reservationOptions: FilterOption[] = [
        enumOption(
            "reservationStatus",
            t("Reservation status"),
            reservationStatusValues(t, RESTAURANT_RESERVATION_STATUSES),
            locale,
        ),
        timestampOption("start", t("Start"), locale),
        timestampOption("end", t("End"), locale),
        ...(restaurants.length ? [enumOption("restaurant", t("Restaurant"), restaurants, locale)] : []),
        ...(tables.length ? [enumOption("table", t("Tables"), tables, locale)] : []),
        numberOption("guestCount", t("Party size"), locale),
    ];

    return [
        ...sections.identity,
        ...sections.accommodation,
        ...reservationOptions,
        ...sections.profile,
    ];
};
